// Receipt PDF generator using jsPDF
// Generates a narrow receipt-format PDF matching the thermal printer output

import jsPDF from 'jspdf';
import { splitTaxInclusive, taxLabel } from './systemFeatures';
import { receiptPaymentLabel, tenderTipTotal } from './paymentMethods';

/**
 * Generate a receipt PDF matching the thermal printer format.
 *
 * @param {Array} orderItems - Order items array
 * @param {number} orderId - Order ID
 * @param {string} tableName - Table name
 * @param {string} orderName - Order/tab name
 * @param {string|null} specialName - Applied promotion name
 * @param {Object} settings - Printer settings from API
 * @param {string|null} closedAt - When the order closed
 * @param {Array} payments - Tenders recorded against the order
 * @returns {jsPDF} The generated PDF document
 */
export const generateReceiptPdf = (orderItems, orderId, tableName, orderName, specialName, settings, closedAt, payments = []) => {
  const businessName = settings?.businessName || 'CERVECERIA GIBBONS';
  const address = settings?.address || 'C. ESCORZA 848';
  const taxId = settings?.taxId || 'CGI181112777';
  const footerMessage1 = settings?.footerMessage1 || '';
  const footerMessage2 = settings?.footerMessage2 || '';
  const footerMessage3 = settings?.footerMessage3 || '';
  const includeIva = settings?.includeIva !== false;

  // Receipt width in mm (80mm standard, 58mm for smaller printers)
  const paperWidthChars = settings?.paperWidth || 32;
  const receiptWidthMm = paperWidthChars >= 42 ? 80 : 58;

  // Calculate height based on content
  const baseHeight = 80; // header + footer
  const itemHeight = 6; // per item line
  const discountItems = orderItems.filter(item => Number(item.discount || 0) > 0).length;
  const extraLines = (specialName ? 15 : 0) + (includeIva ? 12 : 0) +
    (footerMessage1 ? 5 : 0) + (footerMessage2 ? 5 : 0) + (footerMessage3 ? 5 : 0) +
    (payments.length > 0 ? 8 + (payments.length * 4) : 0);
  const totalHeight = baseHeight + (orderItems.length * itemHeight) + (discountItems * 5) + extraLines;

  const doc = new jsPDF({
    unit: 'mm',
    format: [receiptWidthMm, Math.max(totalHeight, 100)]
  });

  const pageWidth = receiptWidthMm;
  const margin = 3;
  const contentWidth = pageWidth - (margin * 2);
  const fontSize = 8;
  const smallFontSize = 7;
  const headerFontSize = 10;

  let y = margin + 2;

  // Helper: center text
  const centerText = (text, yPos, size = fontSize) => {
    doc.setFontSize(size);
    doc.text(text, pageWidth / 2, yPos, { align: 'center' });
  };

  // Helper: left-right aligned text on same line
  const leftRight = (left, right, yPos, size = fontSize) => {
    doc.setFontSize(size);
    doc.text(left, margin, yPos);
    doc.text(right, pageWidth - margin, yPos, { align: 'right' });
  };

  // Helper: draw separator line
  const drawSeparator = (yPos, style = 'solid') => {
    doc.setLineWidth(0.2);
    if (style === 'dashed') {
      doc.setLineDashPattern([1, 1], 0);
    }
    doc.line(margin, yPos, pageWidth - margin, yPos);
    if (style === 'dashed') {
      doc.setLineDashPattern([], 0);
    }
  };

  // --- Header ---
  doc.setFont('courier', 'bold');
  centerText(businessName, y, headerFontSize);
  y += 4;
  doc.setFont('courier', 'normal');
  centerText(address, y, fontSize);
  y += 3.5;
  centerText(taxId, y, fontSize);
  y += 4;
  drawSeparator(y);
  y += 4;

  // --- Order info ---
  doc.setFontSize(fontSize);
  const now = closedAt ? new Date(closedAt) : new Date();
  const dateTime = now.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  doc.text(`Fecha/Hora: ${dateTime}`, margin, y);
  y += 3.5;
  doc.text(`Orden #: ${orderId}`, margin, y);
  y += 3.5;
  doc.text(`Mesa: ${tableName || 'N/A'}`, margin, y);
  y += 3.5;
  if (orderName && orderName !== tableName) {
    doc.text(`Nombre: ${orderName}`, margin, y);
    y += 3.5;
  }

  // --- Promotion ---
  if (specialName) {
    y += 2;
    doc.setFont('courier', 'bold');
    centerText('--- PROMOCION APLICADA ---', y, fontSize);
    y += 3.5;
    centerText(specialName, y, fontSize);
    y += 3.5;
    doc.setFont('courier', 'normal');
  }

  y += 2;

  // --- Items header ---
  drawSeparator(y, 'dashed');
  y += 3.5;
  doc.setFontSize(fontSize);
  doc.text('CANT PRODUCTO', margin, y);
  doc.text('PRECIO', pageWidth - margin, y, { align: 'right' });
  y += 2;
  drawSeparator(y, 'dashed');
  y += 3.5;

  // --- Items ---
  let total = 0;
  let totalDiscount = 0;

  orderItems.forEach(item => {
    const qty = item.quantity;
    const name = item.product_name;
    const price = Number(item.price || 0);
    const discount = Number(item.discount || 0);
    const itemTotal = qty * (price - discount);
    total += itemTotal;

    const maxNameLength = Math.floor(contentWidth / 2);
    const truncatedName = name.length > maxNameLength
      ? name.substring(0, maxNameLength - 2) + '..'
      : name;

    doc.setFontSize(fontSize);
    doc.text(`${String(qty).padStart(2)}x ${truncatedName}`, margin, y);
    doc.text(`$${itemTotal.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
    y += 3.5;

    if (discount > 0) {
      const lineDiscount = discount * qty;
      totalDiscount += lineDiscount;
      doc.setFontSize(smallFontSize);
      doc.text(`    (Descuento: -$${lineDiscount.toFixed(2)})`, margin, y);
      y += 3.5;
    }
  });

  // --- Totals ---
  drawSeparator(y, 'dashed');
  y += 4;

  if (totalDiscount > 0) {
    doc.setFont('courier', 'bold');
    leftRight('Descuento:', `-$${totalDiscount.toFixed(2)}`, y);
    doc.setFont('courier', 'normal');
    y += 5;
  }

  if (includeIva) {
    const { subtotal, tax: ivaAmount } = splitTaxInclusive(total);
    leftRight('Subtotal:', `$${subtotal.toFixed(2)}`, y);
    y += 3.5;
    leftRight(`${taxLabel()}:`, `$${ivaAmount.toFixed(2)}`, y);
    y += 3.5;
  }

  doc.setFont('courier', 'bold');
  leftRight('TOTAL:', `$${total.toFixed(2)}`, y);
  y += 2;
  drawSeparator(y);
  y += 4;
  doc.setFont('courier', 'normal');

  // --- How the check was paid, one line per tender ---
  if (payments.length > 0) {
    doc.text('FORMA DE PAGO', margin, y);
    y += 3.5;
    payments.forEach(payment => {
      leftRight(`${receiptPaymentLabel(payment.payment_type_id)}:`, `$${Number(payment.amount).toFixed(2)}`, y);
      y += 3.5;
    });
    const tipTotal = tenderTipTotal(payments);
    if (tipTotal > 0) {
      leftRight('Propina:', `$${tipTotal.toFixed(2)}`, y);
      y += 3.5;
    }
    drawSeparator(y);
    y += 4;
  }

  // --- Footer ---
  if (footerMessage1) {
    centerText(footerMessage1, y, smallFontSize);
    y += 3.5;
  }
  if (footerMessage2) {
    centerText(footerMessage2, y, smallFontSize);
    y += 3.5;
  }
  if (footerMessage3) {
    y += 2;
    centerText(footerMessage3, y, smallFontSize);
    y += 3.5;
  }

  return doc;
};

/**
 * Generate and open a receipt PDF as a blob URL.
 * On iOS this triggers the share sheet; on desktop it opens in a new tab.
 *
 * @param {Array} orderItems - Order items array
 * @param {number} orderId - Order ID
 * @param {string} tableName - Table name
 * @param {string} orderName - Order/tab name
 * @param {string|null} specialName - Applied promotion name
 * @param {Object} settings - Printer settings from API
 * @param {string|null} closedAt - When the order closed
 * @param {Array} payments - Tenders recorded against the order
 */
export const openReceiptPdf = (orderItems, orderId, tableName, orderName, specialName, settings, closedAt, payments = []) => {
  const doc = generateReceiptPdf(orderItems, orderId, tableName, orderName, specialName, settings, closedAt, payments);
  const pdfBlob = doc.output('blob');
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.open(blobUrl, '_blank');

  // Clean up blob URL after a delay
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};
